import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../hooks/useWebSocket';
import chatApi from '../services/chatApi';
import ChatButton from './ChatButton';
import ChatPanel from './ChatPanel';
import NotificationSettingsModal from './NotificationSettingsModal';

const ChatWidget = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(() => {
    const saved = localStorage.getItem('chatWidgetOpen');
    return saved === 'true';
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);

  const refreshUnreadCount = useCallback(async () => {
    try {
      const count = await chatApi.getUnreadCount();
      setUnreadCount(count);
    } catch (error) {
      // Nie loguj błędów "Request aborted" - to normalne gdy komponent się unmountuje
      const isAborted = error.code === 'ECONNABORTED' || error.message === 'Request aborted' || error.message === 'canceled';
      if (!isAborted) {
        console.error('Error fetching unread count:', error);
      }
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const [usersData, conversations] = await Promise.all([
        chatApi.getUsers(),
        chatApi.getConversations()
      ]);
      
      setUsers(usersData);
      
      // Oblicz liczniki nieprzeczytanych na podstawie conversations (ostatnie wiadomości)
      // conversations zawiera ostatnie wiadomości z każdej konwersacji
      const counts = {};
      conversations.forEach(msg => {
        // Jeśli wiadomość jest dla obecnego użytkownika i nieprzeczytana
        if (msg.recipientId === user?.id && !msg.read) {
          const senderId = msg.senderId;
          counts[senderId] = (counts[senderId] || 0) + 1;
        }
      });
      
      // Dla użytkowników bez wiadomości ustaw 0
      usersData.forEach(u => {
        if (!counts[u.id]) {
          counts[u.id] = 0;
        }
      });
      
      setUnreadCounts(counts);
      return usersData; // Zwróć załadowanych użytkowników
    } catch (error) {
      // Nie loguj błędów "Request aborted" - to normalne gdy komponent się unmountuje
      const isAborted = error.code === 'ECONNABORTED' || error.message === 'Request aborted' || error.message === 'canceled';
      if (!isAborted) {
        console.error('Error loading users:', error);
      }
      // W przypadku błędu, ustaw puste liczniki
      setUnreadCounts({});
      return []; // Zwróć pustą tablicę w przypadku błędu
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Funkcja do otwierania czatu z konkretnym użytkownikiem
  const handleOpenChatWithUser = useCallback((userId) => {
    setIsOpen(true);
    // Jeśli podano userId, znajdź użytkownika i otwórz konwersację
    if (userId) {
      // Sprawdź czy użytkownik jest już w liście
      const userToSelect = users.find(u => u.id === userId);
      if (userToSelect) {
        setSelectedUser(userToSelect);
      } else {
        // Jeśli użytkownik nie jest jeszcze załadowany, załaduj użytkowników i spróbuj ponownie
        loadUsers().then((usersData) => {
          // Po załadowaniu, znajdź użytkownika w zaktualizowanej liście
          const userAfterLoad = usersData.find(u => u.id === userId);
          if (userAfterLoad) {
            setSelectedUser(userAfterLoad);
          }
        });
      }
    }
  }, [users, loadUsers]);

  useEffect(() => {
    if (user) {
      loadUsers();
      refreshUnreadCount();
      
      // Odśwież co 30 sekund
      const interval = setInterval(() => {
        refreshUnreadCount();
        loadUsers();
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, [user, loadUsers, refreshUnreadCount]);

  const handleToggle = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    localStorage.setItem('chatWidgetOpen', newState.toString());
    
    if (newState && selectedUser) {
      // Oznacz wiadomości jako przeczytane gdy otwieramy chat
      refreshUnreadCount();
    }
  };

  const handleSelectUser = (user) => {
    // Jeśli user jest null, oznacza to powrót do listy użytkowników
    if (user === null || user === undefined) {
      setSelectedUser(null);
      return;
    }
    
    // Jeśli user nie ma id, to błąd
    if (!user.id) {
      console.error('handleSelectUser: user has no id', user);
      return;
    }
    
    setSelectedUser(user);
    // Wyczyść licznik nieprzeczytanych dla tego użytkownika
    if (unreadCounts[user.id]) {
      setUnreadCounts(prev => {
        const newCounts = { ...prev };
        delete newCounts[user.id];
        return newCounts;
      });
      refreshUnreadCount();
    }
  };

  // WebSocket hook - musi być po definicji handleOpenChatWithUser
  const { connected, sendMessage, isConnected } = useWebSocket(user?.id, {
    enabled: isOpen,
    isChatMinimized: !isOpen,
    activeConversationUserId: selectedUser?.id,
    onMessage: (message) => {
      // Aktualizuj liczniki nieprzeczytanych
      if (message.recipientId === user?.id && !message.read) {
        setUnreadCounts(prev => ({
          ...prev,
          [message.senderId]: (prev[message.senderId] || 0) + 1
        }));
        refreshUnreadCount();
      }
      
      // Wyślij custom event do ChatWindow (jeśli jest otwarty)
      // ChatWindow nasłuchuje tego eventu i dodaje wiadomość do listy
      window.dispatchEvent(new CustomEvent('chat-message', { detail: message }));
    },
    onOpenChat: handleOpenChatWithUser
  });

  const handleSendMessage = async (recipientId, content) => {
    // Sprawdź czy WebSocket jest połączony
    if (!isConnected && !connected) {
      console.error('WebSocket not connected');
      alert('Brak połączenia z serwerem. Spróbuj odświeżyć stronę.');
      return;
    }

    try {
      const success = sendMessage(recipientId, content);
      if (success) {
        // Wiadomość zostanie dodana przez WebSocket callback
        // Dodaj optymistycznie dla lepszego UX
        const optimisticMessage = {
          id: `temp-${Date.now()}`,
          senderId: user.id,
          recipientId,
          content,
          read: false,
          createdAt: new Date().toISOString(),
          senderName: user.firstName && user.lastName 
            ? `${user.firstName} ${user.lastName}` 
            : user.email,
          senderEmail: user.email
        };
        window.dispatchEvent(new CustomEvent('chat-message', { detail: optimisticMessage }));
      } else {
        console.error('Failed to send message - WebSocket not connected');
        alert('Nie udało się wysłać wiadomości. Sprawdź połączenie z serwerem.');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Wystąpił błąd podczas wysyłania wiadomości: ' + error.message);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <div
        className={`fixed z-50 transition-all duration-300 ${
          isOpen 
            ? 'bottom-0 right-0 md:bottom-5 md:right-5 w-full md:w-[380px] h-full md:h-[600px] md:max-h-[80vh]'
            : 'bottom-5 right-5'
        }`}
      >
        {isOpen ? (
          <div className="h-full w-full">
            <ChatPanel
              users={users}
              selectedUser={selectedUser}
              onSelectUser={handleSelectUser}
              onClose={handleToggle}
              onSendMessage={handleSendMessage}
              unreadCounts={unreadCounts}
              currentUserId={user.id}
              onSettingsClick={() => setShowSettings(true)}
            />
          </div>
        ) : (
          <ChatButton
            onClick={handleToggle}
            unreadCount={unreadCount}
            isOpen={isOpen}
            hasUnread={unreadCount > 0}
          />
        )}
      </div>

      {/* Notification Settings Modal */}
      {showSettings && (
        <NotificationSettingsModal
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
        />
      )}
    </>
  );
};

export default ChatWidget;
